import React from "react";
import { cockpit } from "@/api";
import type { MailboxRefreshView } from "@/api/types";
import { Tooltip } from "@/ds";
import { Icon } from "@/lib";
import { useLiveInvalidation, useLiveScopeKey } from "@/live/live-provider";
import { useSession } from "../session";
import { useT } from "../i18n";

/** Both worklists observe the same tenant operation. Only an explicit click requests IMAP. */
export function MailboxRefresh() {
  const scopeKey = useLiveScopeKey();
  // A new tenant must never render even one frame of the previous tenant's local feedback.
  return <ScopedMailboxRefresh key={scopeKey} scopeKey={scopeKey} />;
}

function ScopedMailboxRefresh({ scopeKey }: { scopeKey: string }) {
  const t = useT();
  const { user, config } = useSession();
  const scopeRef = React.useRef(scopeKey);
  scopeRef.current = scopeKey;
  const sequence = React.useRef(0);
  const requestInFlight = React.useRef(false);
  const mounted = React.useRef(true);
  const [state, setState] = React.useState<{ scope: string; value: MailboxRefreshView } | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [manualOperationId, setManualOperationId] = React.useState<string | null>(null);
  const statusId = React.useId();
  const [now, setNow] = React.useState(Date.now);
  const receipt = state?.scope === scopeKey ? state.value : null;
  const pending = receipt?.status === "queued" || receipt?.status === "running";
  const noContext = !user?.tenantId;
  const inactive = !!user?.tenantId && user.tenantStatus !== "active";
  // #1543: before the configuration answers, whether checking is on is unknown rather than off.
  // Saying "off" until then and nothing afterwards resized the page head under the operator.
  const configKnown = config !== null;
  const disabled = configKnown && config.features?.ingest !== true;
  const eligible = !noContext && !inactive && configKnown && !disabled;

  const load = React.useCallback(async (request: boolean) => {
    // A synchronous guard also covers two clicks before React renders the disabled button.
    // Background GETs must not supersede the POST that establishes manual operation identity.
    if (requestInFlight.current) return;
    const mine = ++sequence.current;
    const scope = scopeKey;
    if (request) {
      requestInFlight.current = true;
      setSubmitting(true);
      setManualOperationId(null);
    }
    const current = () => mounted.current && mine === sequence.current && scopeRef.current === scope;
    try {
      const value = await (request ? cockpit.requestMailboxRefresh() : cockpit.getMailboxRefresh());
      if (!current()) return;
      setState({ scope, value });
      if (request && value.status !== "deferred" && value.status !== "unavailable") {
        setManualOperationId(value.operationId);
      }
      setError(false);
      setNow(Date.now());
    } catch {
      if (current()) setError(true);
    } finally {
      if (request) requestInFlight.current = false;
      if (current()) setSubmitting(false);
    }
  }, [scopeKey]);

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; sequence.current += 1; };
  }, []);
  React.useEffect(() => {
    sequence.current += 1;
    setState(null);
    setError(false);
    setSubmitting(false);
    setManualOperationId(null);
    if (eligible) void load(false);
  }, [scopeKey, eligible, load]);
  const settingsChanged = useLiveInvalidation("settings", "tenants");
  React.useEffect(() => {
    if (eligible && settingsChanged && !submitting && !pending) void load(false);
    // Read availability again when mailbox configuration changes, not on every receipt transition.
  }, [settingsChanged, eligible, load]);
  React.useEffect(() => {
    if (!eligible || !pending || error || submitting) return;
    const timer = window.setTimeout(() => { void load(false); }, 2_000);
    return () => window.clearTimeout(timer);
  }, [receipt, eligible, pending, error, submitting, load]);
  const retryAt = receipt?.retryAfter ? Date.parse(receipt.retryAfter) : 0;
  const coolingDown = retryAt > now;
  React.useEffect(() => {
    if (!coolingDown) return;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, retryAt - Date.now()) + 20);
    return () => window.clearTimeout(timer);
  }, [retryAt, coolingDown]);

  const manualCompletion = receipt?.status === "completed" && !!manualOperationId
    && receipt.operationId === manualOperationId;
  React.useEffect(() => {
    if (!manualCompletion) return;
    const timer = window.setTimeout(() => setManualOperationId(null), 9_000);
    return () => window.clearTimeout(timer);
  }, [manualCompletion, manualOperationId]);

  let message = "";
  if (noContext) message = t("mailboxRefresh.noContext");
  else if (inactive) message = t("mailboxRefresh.inactive");
  else if (disabled) message = t("mailboxRefresh.disabled");
  else if (error) message = t("mailboxRefresh.statusError");
  else if (receipt?.status === "unavailable") {
    message = t(receipt.reason === "processing_unavailable" ? "mailboxRefresh.unavailable"
      : receipt.reason === "no_mailbox" ? "mailboxRefresh.noMailbox"
      : receipt.reason === "tenant_inactive" ? "mailboxRefresh.inactive" : "mailboxRefresh.disabled");
  } else if (manualCompletion) {
    message = t(receipt.newMessages ? "mailboxRefresh.completed" : "mailboxRefresh.empty", { n: receipt.newMessages });
  } else if (receipt?.status === "failed") {
    message = t(receipt.reason === "interrupted" ? "mailboxRefresh.interrupted" : "mailboxRefresh.failed");
  } else if (receipt && (pending || receipt.status === "partial")) {
    message = t(`mailboxRefresh.${receipt.status}`, { n: receipt.newMessages });
  }
  if (submitting) message = t("mailboxRefresh.queued");
  const fault = !submitting && (error || receipt?.status === "failed" || receipt?.status === "partial");
  const unavailable = !eligible || (receipt?.status === "unavailable" && receipt.reason !== "processing_unavailable");
  return (
    <div className="mailbox-refresh">
      {/* #2107: the icon alone; the name lives in the label and the tooltip, the state in the line beside it. */}
      <Tooltip content={t("mailboxRefresh.action")} placement="bottom">
        <button type="button" className="neo-btn neo-btn--ghost neo-btn--sm neo-btn--icon"
          aria-label={t("mailboxRefresh.action")}
          onClick={() => { void load(!error); }}
          disabled={unavailable || submitting || (!error && pending) || coolingDown}
          aria-busy={submitting || (!error && pending)}
          aria-describedby={message ? statusId : undefined}>
          <Icon name="refresh" size={22} aria-hidden="true" />
        </button>
      </Tooltip>
      <span id={statusId} className={`mailbox-refresh__status${fault ? " mailbox-refresh__status--error" : ""}`}
        role="status" aria-live="polite" aria-atomic="true">
        {message}
      </span>
    </div>
  );
}
